import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Create axios instance with credentials
const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Format API error detail
export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Auth Context
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not authenticated
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Add token to requests
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete api.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch (error) {
      setUser(false);
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setToken(data.token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      // Ignore logout errors
    }
    setUser(false);
    setToken(null);
  };

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// API hooks
export { api };

// Tenant API
export const tenantApi = {
  list: (params) => api.get('/tenants', { params }),
  get: (id) => api.get(`/tenants/${id}`),
  create: (data) => api.post('/tenants', data),
  update: (id, data) => api.put(`/tenants/${id}`, data),
  approve: (id) => api.post(`/tenants/${id}/approve`),
  suspend: (id) => api.post(`/tenants/${id}/suspend`),
  activate: (id) => api.post(`/tenants/${id}/activate`),
  register: (data) => api.post('/auth/register-tenant', data),
  resendCredentials: (id) => api.post(`/tenants/${id}/resend-credentials`),
};

// Branch API
export const branchApi = {
  list: (params) => api.get('/branches', { params }),
  get: (id) => api.get(`/branches/${id}`),
  create: (data) => api.post('/branches', data),
  update: (id, data) => api.put(`/branches/${id}`, data),
};

// Staff API
export const staffApi = {
  list: (params) => api.get('/staff', { params }),
  create: (data) => api.post('/staff', data),
  update: (id, data) => api.put(`/staff/${id}`, data),
  getCommissionTiers: (id) => api.get(`/staff/${id}/commission-tiers`),
  updateCommissionTiers: (id, data) => api.put(`/staff/${id}/commission-tiers`, data),
  getCommissionSummary: (id, month) => api.get(`/staff/${id}/commission-summary`, { params: { month } }),
  markIncentivePaid: (id, month) => api.post(`/staff/${id}/incentive-paid`, null, { params: { month } }),
  getMyIncentives: () => api.get('/incentives/my'),
};

// Warranty Plan API
export const warrantyPlanApi = {
  list: (params) => api.get('/warranty-plans', { params }),
  getEligible: (devicePrice, osType) => api.get('/warranty-plans/eligible', { params: { device_price: devicePrice, ...(osType && { os_type: osType }) } }),
  create: (data) => api.post('/warranty-plans', data),
  update: (id, data) => api.put(`/warranty-plans/${id}`, data),
};

// Sales API
export const salesApi = {
  list: (params) => api.get('/sales', { params }),
  get: (id) => api.get(`/sales/${id}`),
  create: (data) => api.post('/sales', data),
  createBulk: (sales) => api.post('/sales/bulk', sales),
  approve: (id) => api.post(`/sales/${id}/approve`),
  reject: (id) => api.post(`/sales/${id}/reject`),
};

// Upload API
export const uploadApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/uploads', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getUrl: (fileId) => `${API_URL}/api/uploads/${fileId}`,
};

// Invoice API
export const invoiceApi = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  generate: (data) => api.post('/invoices/generate', data),
  update: (id, data) => api.put(`/invoices/${id}`, data),
  delete: (id) => api.delete(`/invoices/${id}`),
  recordPayment: (id, data) => api.post(`/invoices/${id}/payment`, data),
  previewCount: (params) => api.get('/invoices/preview-count', { params }),
};

// Compliance API
export const complianceApi = {
  listExceptions: (params) => api.get('/compliance/exceptions', { params }),
  getScore: (tenantId, month) => api.get(`/compliance/score/${tenantId}`, { params: { month } }),
  getBranchScores: (tenantId, month) => api.get(`/compliance/branch-scores/${tenantId}`, { params: { month } }),
};

// Incentives API
export const incentivesApi = {
  list: (params) => api.get('/incentives', { params }),
};

// Dashboard API
export const dashboardApi = {
  superAdmin: () => api.get('/dashboard/super-admin'),
  shopOwner: () => api.get('/dashboard/shop-owner'),
  storeManager: () => api.get('/dashboard/store-manager'),
  salesStaff: () => api.get('/dashboard/sales-staff'),
};

// Analytics API
export const analyticsApi = {
  trends: (months) => api.get('/analytics/trends', { params: { months } }),
};

// Warranty Lookup API
export const warrantyLookupApi = {
  search: (q) => api.get('/warranty-lookup', { params: { q } }),
};

// Reports API
export const reportsApi = {
  warrantySales: (params) => api.get('/reports/warranty-sales', { params }),
  revenue: (params) => api.get('/reports/revenue', { params }),
  outstandingDues: () => api.get('/reports/outstanding-dues'),
  invoiceSummary: (params) => api.get('/reports/invoice-summary', { params }),
};

// Auth API
export const authApi = {
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, newPassword) => api.post('/auth/reset-password', { token, new_password: newPassword }),
};

// Prohibited Devices API
export const prohibitedDevicesApi = {
  list: () => api.get('/prohibited-devices'),
  create: (data) => api.post('/prohibited-devices', data),
  update: (id, data) => api.put(`/prohibited-devices/${id}`, data),
  delete: (id) => api.delete(`/prohibited-devices/${id}`),
  check: (brand, model) => api.get('/prohibited-devices/check', { params: { brand, model } }),
};

// Claims API
export const claimsApi = {
  list: (params) => api.get('/claims', { params }),
  get: (id) => api.get(`/claims/${id}`),
  create: (data) => api.post('/claims', data),
  update: (id, data) => api.put(`/claims/${id}`, data),
  trackPublic: (claimId) => api.get('/claims/public/track', { params: { claim_id: claimId } }),
};

// Inquiries API
export const inquiriesApi = {
  list: (params) => api.get('/inquiries', { params }),
  update: (id, data) => api.put(`/inquiries/${id}`, data),
};

// User Management API
export const userApi = {
  resendCredentials: (id) => api.post(`/users/${id}/resend-credentials`),
};

// Phone Brands API
export const phoneBrandsApi = {
  list: () => api.get('/phone-brands'),
  listAll: () => api.get('/phone-brands/all'),
  create: (data) => api.post('/phone-brands', data),
  update: (id, data) => api.put(`/phone-brands/${id}`, data),
  delete: (id) => api.delete(`/phone-brands/${id}`),
};

// Chat API
export const chatApi = {
  list: (params) => api.get('/chats', { params }),
  get: (id) => api.get(`/chats/${id}`),
  create: (data) => api.post('/chats', data),
  getMessages: (id, params) => api.get(`/chats/${id}/messages`, { params }),
  sendMessage: (id, data) => api.post(`/chats/${id}/messages`, data),
  updateStatus: (id, data) => api.put(`/chats/${id}/status`, data),
  assign: (id, data) => api.put(`/chats/${id}/assign`, data),
  getUserContext: (id) => api.get(`/chats/${id}/user-context`),
  rate: (id, data) => api.post(`/chats/${id}/rate`, data),
  getUnreadCount: () => api.get('/chats/unread-count'),
};

// Support API
export const supportApi = {
  listAgents: (params) => api.get('/support/agents', { params }),
  createAgent: (data) => api.post('/support/agents', data),
  updateAgent: (id, data) => api.put(`/support/agents/${id}`, data),
  deleteAgent: (id) => api.delete(`/support/agents/${id}`),
  resetPassword: (id, data) => api.put(`/support/agents/${id}/reset-password`, data),
  getAnalytics: () => api.get('/support/analytics'),
};

// Chat Files API
export const chatFilesApi = {
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/chat-files/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  getUrl: (fileId) => `${API_URL}/api/chat-files/${fileId}?auth=${localStorage.getItem('token')}`,
};

// Modification Requests API
export const approvalsApi = {
  list: (params) => api.get('/modification-requests', { params }),
  get: (id) => api.get(`/modification-requests/${id}`),
  create: (data) => api.post('/modification-requests', data),
  review: (id, data) => api.put(`/modification-requests/${id}/review`, data),
  pendingCount: () => api.get('/modification-requests/pending-count'),
};

// WebSocket URL builder
export function getWebSocketUrl(token) {
  const base = API_URL.replace(/^http/, 'ws');
  return `${base}/api/ws/chat?token=${token}`;
}
